import { reportApiError } from './errorReporter';

const AUTH_URL = import.meta.env.VITE_AUTH_URL;
const API_URL = import.meta.env.VITE_API_URL;

class ApiError extends Error {
  constructor(message, status, requestId, traceId, data = null) {
    super(message);
    this.status = status;
    this.requestId = requestId;
    this.traceId = traceId;
    this.data = data; // Full error response from server (for structured errors like auth_required)
  }
}

let fallbackRequestCounter = 0;

function generateRequestId() {
  try {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return globalThis.crypto.randomUUID();
    }
  } catch {
    // Try getRandomValues below in restricted browser contexts.
  }

  const bytes = new Uint8Array(16);
  try {
    if (typeof globalThis.crypto?.getRandomValues === 'function') {
      globalThis.crypto.getRandomValues(bytes);
    } else {
      // Request IDs are correlation identifiers, not secrets; retain uniqueness
      // in older/non-secure contexts by mixing time, a local counter and random bytes.
      let time = Date.now();
      fallbackRequestCounter += 1;
      for (let i = 0; i < bytes.length; i += 1) {
        bytes[i] = (Math.floor(Math.random() * 256) ^ (time & 0xff) ^ (fallbackRequestCounter & 0xff)) & 0xff;
        time = Math.floor(time / 256);
      }
    }
  } catch {
    let time = Date.now();
    fallbackRequestCounter += 1;
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = (Math.floor(Math.random() * 256) ^ (time & 0xff) ^ (fallbackRequestCounter & 0xff)) & 0xff;
      time = Math.floor(time / 256);
    }
  }

  // Mark as a UUID v4 with the RFC 4122 variant bits.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

async function request(url, options = {}) {
  const requestId = options.requestId || generateRequestId();
  const traceId = options.traceId || requestId;
  const startTime = Date.now();

  // Build headers with optional tenant ID
  const headers = {
    'Content-Type': 'application/json',
    'X-Request-ID': requestId,
    'X-Trace-ID': traceId,
  };

  // Add tenant ID header if provided
  if (options.tenantId) {
    headers['X-Tenant-ID'] = options.tenantId;
  }

  const config = {
    ...options,
    credentials: 'include', // Send cookies
    headers: {
      ...headers,
      ...options.headers,
      // Keep the correlation identifiers canonical even when callers add headers.
      'X-Request-ID': requestId,
      'X-Trace-ID': traceId,
    },
  };

  // Pass through AbortController signal if provided
  if (options.signal) {
    config.signal = options.signal;
  }

  try {
    const response = await fetch(url, config);
    const duration = Date.now() - startTime;

    if (import.meta.env.DEV) {
      console.log('[API]', {
        traceId,
        requestId,
        method: config.method || 'GET',
        url,
        status: response.status,
        duration: `${duration}ms`,
      });
    }

    if (response.status === 401) {
      // Unauthorized - let the calling code handle this
      // (e.g., authSlice sets isAuthenticated=false, React Router redirects)
      throw new ApiError('Unauthorized', 401, requestId, traceId);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      // Report bug-category errors to the error queue (fire and forget).
      reportApiError(errorData.error, errorData.message || errorData.error, {
        url,
        status: response.status,
        request_id: requestId,
        trace_id: traceId,
      });
      throw new ApiError(errorData.error || errorData.message || 'Request failed', response.status, requestId, traceId, errorData);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null;
    }

    return response.json();
  } catch (error) {
    if (!(error instanceof ApiError)) {
      console.error('[API ERROR]', {
        traceId,
        requestId,
        url,
        method: config.method || 'GET',
        error: error.message,
      });
    }
    throw error;
  }
}

export const api = {
  get: (url, options = {}) => request(url, { ...options, method: 'GET' }),
  post: (url, body, options = {}) =>
    request(url, { ...options, method: 'POST', body: JSON.stringify(body) }),
  put: (url, body, options = {}) =>
    request(url, { ...options, method: 'PUT', body: JSON.stringify(body) }),
  delete: (url, options = {}) => request(url, { ...options, method: 'DELETE' }),
};

export function createAbortController() {
  return new AbortController();
}

export { AUTH_URL, API_URL };
